// MYNDIGHET & ROE — matrisa over beslutningsrett, fra nasjonalt nivå til
// enkeltmann, og delegasjonene som flytter den. Docked GNÅ-panel, ren DOM
// (all felt-tekst via textContent — aldri innerHTML på brukerdata).
//
// Eiers bestilling: «autorisasjonene og ROE ligger inne som en matrise — fra
// kongen til Ola enkeltmann … tett knyttet til ORBAT». Motoren ligger i
// contracts (`myndighet.ts`): katalog, delegasjonsposter, kanDelegere/
// harMyndighet/matriseCelle. Dette panelet er visningen og handlingene:
//   fane 1 MATRISE     — hele bildet: hvem har hva, iboende/delegert/mulig.
//   fane 2 DELEGASJONER — din enhets status, aktive delegasjoner, ny/trekk.
// Hver utstedelse og tilbaketrekking går til hendelsesloggen — sporet er
// kontrollen, ikke en underskrift (eiers ord).
import {
  MYNDIGHETER, KATEGORI_LABEL, NIVAA_ORDER, NIVAA_LABEL, nivaaRank,
  kanDelegere, harMyndighet, erAktiv, kanTrekke, matriseCelle, antallDelegertTil,
  DelegasjonSchema, addressLabel,
  type Delegasjon, type MyndighetsNivaa, type MyndighetsKategori,
  type UnitAddress, type Echelon, ECHELON_LABEL, ECHELON_ORDER,
} from '@skade/contracts';
import { canDelegateAuthority, type Role, type OrgUnit } from '@skade/rbac';

export interface MyndighetSelf {
  id: string;
  role: Role;
  org: OrgUnit;
  /** Enhetens ORBAT-adresse (echelon + territorium + kommandosti). */
  enhet: UnitAddress;
  /** Beslutningsnivået (FOH = nasjonal — over echelon-aksen). */
  nivaa: MyndighetsNivaa;
}

export interface MyndighetHooks {
  self: MyndighetSelf;
  getDelegasjoner: () => Delegasjon[];
  /** Kringkast en ny/trukket delegasjonspost (hubben stempler utsteder). */
  publish: (d: Delegasjon) => void;
  naa: () => number;
}

/** Utløpsvalg. «Til trukket» er standard: en glemt tidsfrist som stille dreper
 * en delegasjon midt i en operasjon er farligere enn en som må trekkes aktivt. */
const UTLOP: Array<{ v: string; t: string; ms?: number }> = [
  { v: '0', t: 'Til trukket' },
  { v: '6', t: '6 timer', ms: 6 * 3_600_000 },
  { v: '12', t: '12 timer', ms: 12 * 3_600_000 },
  { v: '24', t: '1 døgn', ms: 24 * 3_600_000 },
  { v: '168', t: '1 uke', ms: 168 * 3_600_000 },
];

type Fane = 'matrise' | 'delegasjoner';

export class MyndighetPanel {
  readonly el: HTMLElement;
  private open = false;
  private fane: Fane = 'matrise';
  private bodyEl!: HTMLElement;
  private faneEl!: HTMLElement;

  constructor(private hooks: MyndighetHooks) {
    this.el = this.build();
    this.render();
  }

  toggle() {
    this.open = !this.open;
    this.el.classList.toggle('open', this.open);
    if (this.open) this.render();
  }
  close() { if (this.open) this.toggle(); }

  render() {
    if (!this.bodyEl) return;
    this.bodyEl.textContent = '';
    this.renderFaner();
    if (this.fane === 'matrise') this.renderMatrise();
    else this.renderDelegasjoner();
  }

  private build(): HTMLElement {
    const p = div('report-composer myndighet-panel');
    const head = div('report-head');
    head.append(span('report-title', 'Myndighet & ROE'));
    const x = document.createElement('button');
    x.className = 'report-x'; x.textContent = '×'; x.onclick = () => this.toggle();
    head.append(x);
    const faner = div('myndighet-faner');
    const body = div('myndighet-body');
    p.append(head, faner, body);
    this.faneEl = faner;
    this.bodyEl = body;
    return p;
  }

  private renderFaner() {
    this.faneEl.textContent = '';
    for (const [id, t] of [['matrise', 'Matrise'], ['delegasjoner', 'Delegasjoner']] as Array<[Fane, string]>) {
      const b = document.createElement('button');
      b.className = 'myndighet-fane' + (this.fane === id ? ' aktiv' : '');
      b.textContent = t;
      b.onclick = () => { this.fane = id; this.render(); };
      this.faneEl.append(b);
    }
  }

  // ── Fane 1: matrisa ─────────────────────────────────────────────────────────
  //
  // Kolonner høyeste→laveste (Nasjonal … Enkeltmann) — man leser beslutningsrett
  // ovenfra, som i en kommandokjede. Egen kolonne utheves så operatøren finner
  // sitt eget nivå uten å telle.
  private renderMatrise() {
    const naa = this.hooks.naa();
    const deleg = this.hooks.getDelegasjoner();
    const kolonner = [...NIVAA_ORDER].reverse();

    const wrap = div('myndighet-scroll');
    const tbl = document.createElement('table');
    tbl.className = 'myndighet-tabell';

    const thead = document.createElement('thead');
    const hr = document.createElement('tr');
    hr.append(th(''));
    for (const n of kolonner) {
      const c = th(kortNivaa(n));
      c.title = NIVAA_LABEL[n];
      if (n === this.hooks.self.nivaa) c.classList.add('egen');
      hr.append(c);
    }
    thead.append(hr);
    tbl.append(thead);

    const tbody = document.createElement('tbody');
    let forrige: MyndighetsKategori | null = null;
    for (const m of MYNDIGHETER) {
      if (m.kategori !== forrige) {
        forrige = m.kategori;
        const kr = document.createElement('tr');
        const kc = document.createElement('td');
        kc.className = 'myndighet-kategori';
        kc.colSpan = kolonner.length + 1;
        kc.textContent = KATEGORI_LABEL[m.kategori];
        kr.append(kc);
        tbody.append(kr);
      }
      const r = document.createElement('tr');
      const navn = document.createElement('td');
      navn.className = 'myndighet-navn';
      navn.textContent = m.label;
      navn.title = m.forklaring;
      r.append(navn);
      for (const n of kolonner) {
        const celle = matriseCelle(m, n, deleg, naa);
        const td = document.createElement('td');
        td.className = `myndighet-celle ${celle}`;
        if (n === this.hooks.self.nivaa) td.classList.add('egen');
        if (celle === 'iboende') { td.textContent = '●'; td.title = `${m.label}: iboende på ${NIVAA_LABEL[n]}`; }
        else if (celle === 'delegert') {
          const antall = antallDelegertTil(m, n, deleg, naa);
          td.textContent = antall > 1 ? `✓${antall}` : '✓';
          td.title = `${m.label}: ${antall} aktiv${antall === 1 ? '' : 'e'} delegasjon${antall === 1 ? '' : 'er'} til ${NIVAA_LABEL[n]} — se Delegasjoner-fanen for vilkår`;
        } else if (celle === 'delegerbar') { td.textContent = '○'; td.title = `${m.label}: kan delegeres til ${NIVAA_LABEL[n]}`; }
        else { td.textContent = '·'; td.title = `${m.label}: ikke tilgjengelig på ${NIVAA_LABEL[n]}`; }
        r.append(td);
      }
      tbody.append(r);
    }
    tbl.append(tbody);
    wrap.append(tbl);

    const legende = div('myndighet-legende');
    legende.append(
      span('myndighet-leg iboende', '● iboende'),
      span('myndighet-leg delegert', '✓ delegert'),
      span('myndighet-leg delegerbar', '○ kan delegeres'),
      span('myndighet-leg ingen', '· ikke tilgjengelig'),
    );
    this.bodyEl.append(wrap, legende);
  }

  // ── Fane 2: delegasjoner ────────────────────────────────────────────────────
  private renderDelegasjoner() {
    const naa = this.hooks.naa();
    const self = this.hooks.self;
    const alle = this.hooks.getDelegasjoner();

    // Din enhets stilling — svaret på «hva kan JEG nå?», uten å lese matrisa.
    const egen = div('myndighet-egen');
    egen.append(span('myndighet-egen-t', `Din enhet: ${addressLabel(self.enhet)} · nivå ${NIVAA_LABEL[self.nivaa]}`));
    const mine: string[] = [];
    for (const m of MYNDIGHETER) {
      const st = harMyndighet(m.id, self.enhet, self.nivaa, alle, naa);
      if (st.status === 'iboende') mine.push(m.label);
      else if (st.status === 'delegert') mine.push(`${m.label} (delegert${st.delegasjon?.vilkaar ? ` — ${st.delegasjon.vilkaar}` : ''})`);
    }
    egen.append(span('myndighet-egen-liste', mine.length ? mine.join(' · ') : 'Ingen myndigheter på dette nivået.'));
    this.bodyEl.append(egen);

    // Aktive delegasjoner — nyeste først. Vilkårene vises ALLTID: en delegasjon
    // uten synlige vilkår leses som ubegrenset, og det er den sjelden ment som.
    const aktive = alle.filter((d) => erAktiv(d, naa)).sort((a, b) => b.tsMs - a.tsMs);
    this.bodyEl.append(span('checklist-cat', `Aktive delegasjoner (${aktive.length})`));
    if (!aktive.length) {
      this.bodyEl.append(span('myndighet-tom', 'Ingen aktive delegasjoner — all myndighet følger matrisas iboende nivåer.'));
    }
    for (const d of aktive) {
      const m = MYNDIGHETER.find((x) => x.id === d.myndighetId);
      const rad = div('myndighet-rad');
      const hoved = div('myndighet-rad-hoved');
      hoved.append(span('myndighet-rad-t', `${m?.label ?? d.myndighetId} → ${addressLabel(d.til)}`));
      const detaljer: string[] = [`Utstedt av ${d.utstedtAv} (${NIVAA_LABEL[d.utstederNivaa]})`];
      if (d.vilkaar) detaljer.push(`Vilkår: ${d.vilkaar}`);
      detaljer.push(d.utloperMs ? `Utløper ${new Date(d.utloperMs).toISOString().slice(0, 16).replace('T', ' ')}Z` : 'Til trukket');
      hoved.append(span('myndighet-rad-d', detaljer.join(' · ')));
      rad.append(hoved);
      if (kanTrekke(d, self.nivaa) && canDelegateAuthority({ id: self.id, role: self.role, org: self.org }, d.til)) {
        const t = document.createElement('button');
        t.className = 'targetlist-btn'; t.textContent = 'Trekk';
        t.onclick = () => {
          // Immutabel trekking: NY post med trukketMs — historikken består.
          this.hooks.publish({ ...d, trukketMs: this.hooks.naa(), trukketAv: self.id });
          this.render();
        };
        rad.append(t);
      }
      this.bodyEl.append(rad);
    }

    this.renderNyDelegasjon(naa);
  }

  private renderNyDelegasjon(naa: number) {
    const self = this.hooks.self;
    // Bare myndigheter utstederen faktisk KAN gi: delegerbare, iboende på/under
    // eget nivå. En select full av valg som alltid avvises lærer operatøren at
    // panelet ikke virker.
    const mineDelegerbare = MYNDIGHETER.filter((m) =>
      m.delegerbar && nivaaRank(self.nivaa) >= nivaaRank(m.iboendeFra));
    this.bodyEl.append(span('checklist-cat', 'Ny delegasjon'));
    if (!mineDelegerbare.length) {
      this.bodyEl.append(span('myndighet-tom', `${NIVAA_LABEL[self.nivaa]} har ingen iboende myndigheter å delegere. Delegert myndighet kan ikke videredelegeres.`));
      return;
    }

    const form = div('eso-dist-form');
    const mynSel = sel(mineDelegerbare.map((m) => ({ v: m.id, t: m.label })));
    const forklaring = span('myndighet-forklaring', '');
    const oppdaterForklaring = () => {
      const m = MYNDIGHETER.find((x) => x.id === mynSel.value);
      forklaring.textContent = m ? `${m.forklaring} Gulv: ${NIVAA_LABEL[m.lavesteNivaa]}.` : '';
    };
    mynSel.onchange = oppdaterForklaring;

    // Mottakernivå: bare echelons UNDER utstederens nivå (delegasjon går nedover).
    const nivaaValg = ECHELON_ORDER.filter((e) => nivaaRank(e) < nivaaRank(self.nivaa)).reverse();
    const lvlSel = sel(nivaaValg.map((e) => ({ v: e, t: ECHELON_LABEL[e] })));
    const district = inp('Distrikt (HV-xx)', self.org.district ?? '');
    const area = inp('Område', self.org.area ?? '');
    const troop = inp('Tropp', '');
    const team = inp('Lag', '');
    const vilkaar = inp('Vilkår («kun i eget AO», «kun objekt X») — valgfri', '');
    const utlopSel = sel(UTLOP.map((u) => ({ v: u.v, t: u.t })));
    form.append(
      labeled('Myndighet', mynSel), forklaring, labeled('Til nivå', lvlSel),
      district.wrap, area.wrap, troop.wrap, team.wrap, vilkaar.wrap, labeled('Utløp', utlopSel),
    );
    oppdaterForklaring();

    const result = div('admin-result');
    const btn = document.createElement('button');
    btn.className = 'report-submit'; btn.textContent = 'Deleger myndighet';
    btn.onclick = () => {
      result.textContent = '';
      const til: UnitAddress = {
        echelon: lvlSel.value as Echelon,
        district: district.control.value.trim() || undefined,
        area: area.control.value.trim() || undefined,
        troop: troop.control.value.trim() || undefined,
        team: team.control.value.trim() || undefined,
      };
      // Begge halvdelene av gaten, med grunnen synlig. Klient-forhåndssjekk —
      // hubben validerer og stempler utsteder autoritativt ved fanout.
      const nivaaSvar = kanDelegere(mynSel.value, self.nivaa, til);
      if (!nivaaSvar.ok) { result.append(span('admin-err', `Avvist: ${nivaaSvar.grunn}`)); return; }
      if (!canDelegateAuthority({ id: self.id, role: self.role, org: self.org }, til)) {
        result.append(span('admin-err', 'Avvist: mottakeren ligger utenfor ditt org-scope.'));
        return;
      }
      const ms = UTLOP.find((u) => u.v === utlopSel.value)?.ms;
      const parsed = DelegasjonSchema.safeParse({
        id: `dg-${Date.now().toString(36)}-${Math.floor(performance.now() % 1e6).toString(36)}`,
        myndighetId: mynSel.value,
        til,
        vilkaar: vilkaar.control.value.trim() || undefined,
        utstedtAv: self.id,
        utstederNivaa: self.nivaa,
        tsMs: this.hooks.naa(),
        utloperMs: ms ? naa + ms : undefined,
      });
      if (!parsed.success) { result.append(span('admin-err', 'Ugyldig delegasjon.')); return; }
      this.hooks.publish(parsed.data);
      this.render();
    };
    form.append(btn);
    this.bodyEl.append(form, result);
  }
}

// ── Småhjelpere (samme mønster som ESO/RFI-panelene) ────────────────────────

function kortNivaa(n: MyndighetsNivaa): string {
  if (n === 'nasjonal') return 'NASJ';
  const kort: Record<string, string> = {
    enkeltmann: 'ENK', makkerpar: 'MAK', lag: 'LAG', tropp: 'TR',
    kompani: 'KP', bataljon: 'BN', brigade: 'BDE',
  };
  return kort[n] ?? n;
}

function div(cls: string): HTMLElement { const e = document.createElement('div'); e.className = cls; return e; }
function span(cls: string, text: string): HTMLElement { const e = document.createElement('span'); e.className = cls; e.textContent = text; return e; }
function th(text: string): HTMLTableCellElement { const e = document.createElement('th'); e.textContent = text; return e; }
function sel(opts: Array<{ v: string; t: string }>): HTMLSelectElement {
  const s = document.createElement('select'); s.className = 'checklist-sel';
  for (const o of opts) { const el = document.createElement('option'); el.value = o.v; el.textContent = o.t; s.append(el); }
  return s;
}
function inp(placeholder: string, value: string): { wrap: HTMLElement; control: HTMLInputElement } {
  const control = document.createElement('input');
  control.className = 'report-input'; control.placeholder = placeholder; control.value = value;
  return { wrap: labeled(placeholder, control), control };
}
function labeled(label: string, control: HTMLElement): HTMLElement {
  const wrap = div('report-field');
  wrap.append(span('report-field-label', label), control);
  return wrap;
}
